// This file is DELIBERATELY EXEMPT from the dark-mode token migration.
// BuildingReportView is rendered on two paths that must both stay a fixed
// light document regardless of the viewer's OS/app theme:
//   1. The interactive Report tab (BuildingDetail.tsx) — a UI screen.
//   2. The headless /print/building-report route, which workers/pdf-generator
//      snapshots straight to a customer-facing PDF invoice. There is no
//      concept of "dark mode" for a PDF a tenant receives by email.
// Every colour in this file (backgrounds, borders, text) is therefore a
// literal Tailwind class or hex value, NEVER a `bg-surface`/`text-content`
// token and NEVER a `dark:` variant. The root of BuildingReportView also
// carries an explicit inline `color: '#111827'` so it never depends on the
// inherited `body { color: var(--text) }` from src/index.css — see
// PrintBuildingReport.tsx and BuildingDetail.tsx:410 for the two call sites,
// and src/test/contrast.test.ts for the regression test. If you're doing a
// future dark-mode sweep and this file's literal colours look like
// leftovers: they are not. Tokenising them breaks the PDF.
import { useState, useEffect } from 'react';
import { api } from '../api/client';
import type { BuildingReport as ReportData } from '../api/types';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { fmtCurrency } from '../lib/fmt';

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];

interface Props {
  buildingId: string;
}

export default function BuildingReport({ buildingId }: Props) {
  const [data, setData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadReport();
  }, [buildingId]);

  const loadReport = async () => {
    try {
      setLoading(true);
      const res = await api.getBuildingReport(buildingId);
      setData(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-20 bg-gray-200 rounded-xl"></div>
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-24 bg-gray-200 rounded-xl"></div>)}
        </div>
        <div className="h-64 bg-gray-200 rounded-xl"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4">
        <p className="text-red-800 text-sm">{error}</p>
      </div>
    );
  }

  if (!data || data.pockets.length === 0 || !data.period_start || !data.period_end) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-500">
        <p className="text-lg font-medium mb-1">No billing data available</p>
        <p className="text-sm">Finalize a bill run for pockets in this building to see the report.</p>
      </div>
    );
  }

  return <BuildingReportView data={data} />;
}

type ReportPocket = ReportData['pockets'][number];

// The pocket's tariff names as pills, or '-' when there is none. The API joins
// a pocket's tariffs with ', '; each becomes its own pill, so the cell wraps
// between names (one name used to wrap word by word down five lines of the PDF).
// The list the API sends keeps a name with a comma whole; only a report
// stored before 2026-10-15 falls back to splitting the joined text.
// On screen a long name may still wrap inside its pill, so a phone's table
// does not grow wider than the screen; print keeps each name whole.
function tariffPill(names: string | readonly string[] | undefined, print = false) {
  // A list (the API since 2026-10-15) keeps each name whole, commas and all;
  // a report stored before then carries only the joined text.
  const list = typeof names === 'string' ? (names ? names.split(', ') : []) : (names ?? []);
  if (list.length === 0) return '-';
  return (
    <span className="inline-flex flex-wrap gap-1">
      {list.map(n => (
        <span key={n} className={`px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full${print ? ' whitespace-nowrap' : ''}`}>{n}</span>
      ))}
    </span>
  );
}

const hasWater = (p: ReportPocket) => (p.current_kl || 0) > 0 || (p.common_area_kl || 0) > 0;
// kL is small (0.1 to a few hundred), so 2 decimals, not kWh's whole units.
const round2 = (n: number) => Math.round(n * 100) / 100;

/** Water bar chart rows: own use with the common-area share stacked on it. */
export function waterBarRows(pockets: readonly ReportPocket[]): Array<{ name: string; own: number; ca: number }> {
  return pockets
    .filter(hasWater)
    .map(p => ({
      name: (p.shop_no || p.tenant_name || p.pocket_name).substring(0, 12),
      own: round2(p.current_kl || 0),
      ca: round2(p.common_area_kl || 0),
    }))
    .sort((a, b) => (b.own + b.ca) - (a.own + a.ca));
}

// Pure presentational report body. Rendered by the interactive Report tab AND by
// the headless /print/building-report page, which the pdf-generator's Browser
// Rendering snapshots to a PDF. Chart animation is disabled (isAnimationActive
// = false throughout) so a headless capture never grabs a mid-animation frame.
//
// layout="print" (the print page): Browser Rendering prints A4 at ~718 CSS px,
// below the md/lg breakpoints, so the responsive classes gave the PDF the
// phone layout (headline figures stacked down page 1, charts measured at half
// width). The print layout uses no breakpoints, and every card carries
// break-inside-avoid so a page break falls between cards, never inside one
// (Beau, 2026-10-15: sections were cut across pages).
export function BuildingReportView({ data, layout = 'screen' }: { data: ReportData; layout?: 'screen' | 'print' }) {
  const print = layout === 'print';
  // A card the page must not split. The marker lets tests find every card.
  const block = (cls: string) => ({ 'data-report-block': '', className: print ? `${cls} break-inside-avoid` : cls });
  const fmtNum = (n: number, d = 0) => n.toLocaleString('en-ZA', { minimumFractionDigits: d, maximumFractionDigits: d });

  // Windowed (time-based) energy reports relabel amounts as energy cost and show
  // the actual window instead of a billing period.
  const isWindow = data.basis === 'window';
  const fmtSastDT = (iso: string) => {
    try {
      return new Date(iso).toLocaleString('en-ZA', { timeZone: 'Africa/Johannesburg', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  // Pocket classification — shared between the categorized tables below and
  // the occupancy pie chart. Mirrors the same rule.
  const isSupply = (p: typeof data.pockets[number]) => p.category === 'supply';
  const isVacant = (p: typeof data.pockets[number]) =>
    !isSupply(p) && (!p.tenant_name || p.tenant_name.toLowerCase() === 'vacant');
  const isActive = (p: typeof data.pockets[number]) =>
    !isSupply(p) && !isVacant(p);

  // Bar chart data — consumer pockets only (Supply pockets show bulk totals
  // that would dwarf tenant consumption). Each bar is stacked: tenant's own
  // metered usage + the CA portion allocated to them via ca_percent.
  const consumers = data.pockets.filter(p => !isSupply(p));
  const elecBarData = consumers
    .filter(p => p.current_kwh > 0 || p.common_area_kwh > 0)
    .map(p => ({
      name: (p.shop_no || p.tenant_name || p.pocket_name).substring(0, 12),
      own: Math.round(p.current_kwh),
      ca: Math.round(p.common_area_kwh || 0),
    }))
    .sort((a, b) => (b.own + b.ca) - (a.own + a.ca));
  const waterBarData = waterBarRows(consumers);

  const totalAmount = data.pockets.reduce((s, p) => s + p.total_amount_cents, 0);
  // Period-over-period compares like with like: only pockets with a previous
  // figure, in both columns and the total (Buttanut, 2026-10-15: one pocket's
  // previous kWh against five pockets' current printed +31.5% for a 34% fall).
  const comparable = data.pockets.filter(p => p.prev_kwh !== null);
  // Windowed reports: meters the amounts leave out (no tariff in force for the
  // window, or a time-of-use meter without half-hourly data).
  const unpricedCount = data.pockets.reduce((s, p) => s + (p.unpriced_count ?? 0), 0);
  const amount = (p: ReportPocket) => `${fmtCurrency(p.total_amount_cents)}${p.unpriced_count ? '*' : ''}`;
  const pieData = data.pockets.filter(p => p.total_amount_cents > 0).map(p => ({
    name: (p.tenant_name || p.pocket_name).substring(0, 20),
    value: p.total_amount_cents / 100,
  }));

  // Occupancy: active vs vacant. Excludes supply pockets — they aren't
  // tenant units, so counting them as "vacant" would be misleading.
  const activeCount = data.pockets.filter(isActive).length;
  const vacantCount = data.pockets.filter(isVacant).length;
  const occupancyData = [
    { name: 'Active', value: activeCount },
    { name: 'Vacant', value: vacantCount },
  ].filter(d => d.value > 0);
  const OCCUPANCY_COLORS: Record<string, string> = { Active: '#10B981', Vacant: '#F59E0B' };
  // Four tiles share a 190 mm row in print, so they are tighter there.
  const tile = print ? 'p-3' : 'p-5';
  // Seven columns of 14 px text with 16 px padding do not fit 190 mm once the
  // amounts and tariff names stop wrapping; print sets tables at 12 px.
  const pad = print ? 'px-2 py-1.5' : 'px-4 py-2';
  const tableText = print ? 'text-xs' : 'text-sm';
  const tileValue = print ? 'text-xl whitespace-nowrap' : 'text-2xl';

  return (
    // Explicit literal `color` so this tree NEVER inherits `body`'s
    // themed `color: var(--text)` (near-white in dark mode) — this is the
    // fix for the C1 contrast collapse. Matches the pre-token-migration
    // `body { @apply text-gray-900 }` value exactly (#111827), independent
    // of whatever renders this component (interactive tab or print route).
    <div className={print ? 'space-y-4' : 'space-y-6'} style={{ color: '#111827' }}>
      {/* Header Banner */}
      <div {...block('bg-gradient-to-r from-slate-800 to-slate-700 rounded-xl p-6 text-white')}>
        <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">{isWindow ? 'Usage Summary' : 'Utility Consumption Report'}</p>
        <h2 className="text-xl font-bold">{data.building_name}</h2>
        <div className="flex items-center gap-6 mt-2 text-sm text-slate-300">
          {isWindow ? (
            <span>Window: {fmtSastDT(data.window_start || data.period_start)} – {fmtSastDT(data.window_end || data.period_end)} (SAST)</span>
          ) : (
            <>
              <span>Billing Period: {data.period_start} to {data.period_end}</span>
              <span>{data.billing_days} days</span>
              <span>Report Type: Monthly Consumption Analysis</span>
            </>
          )}
        </div>
      </div>

      {data.energy_only && (
        <div {...block('bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-xs text-amber-800')}>
          <span className="font-semibold">Usage summary.</span> Shows consumption &amp; cost (electricity energy + water) for the window only.
          Monthly capacity &amp; fixed charges (maximum demand, network capacity, fixed/service) are excluded: they apply to a whole billing month, not a partial window.
        </div>
      )}

      {unpricedCount > 0 && (
        <div {...block('bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-xs text-amber-800')}>
          {unpricedCount} {unpricedCount === 1 ? 'meter' : 'meters'} could not be priced: no tariff in force for the window, or a
          time-of-use meter without half-hourly readings, or water at a restriction level the tariff has no rate for. The amounts marked * leave out what could not be priced.
        </div>
      )}

      {/* Summary Tiles */}
      <div data-testid="report-tiles" {...block(print ? `grid ${data.total_kl > 0 ? 'grid-cols-4' : 'grid-cols-3'} gap-3` : 'grid grid-cols-1 md:grid-cols-4 gap-4')}>
        <div className={`bg-blue-50 border border-blue-200 rounded-xl ${tile}`}>
          <p className="text-xs uppercase tracking-wider text-blue-600 mb-1">Total Electricity</p>
          <p className={`${tileValue} font-bold text-blue-700`}>{fmtNum(data.total_kwh)} kWh</p>
          <p className="text-xs text-blue-500 mt-1">{data.meter_count} meter{data.meter_count !== 1 ? 's' : ''}</p>
        </div>
        <div className={`bg-indigo-50 border border-indigo-200 rounded-xl ${tile}`}>
          <p className="text-xs uppercase tracking-wider text-indigo-600 mb-1">Commercial Tenants</p>
          <p className={`${tileValue} font-bold text-indigo-700`}>{data.pocket_count}</p>
          <p className="text-xs text-indigo-500 mt-1">sub-metered units</p>
        </div>
        {data.total_kl > 0 && (
          <div className={`bg-green-50 border border-green-200 rounded-xl ${tile}`}>
            <p className="text-xs uppercase tracking-wider text-green-600 mb-1">Water Consumption</p>
            <p className={`${tileValue} font-bold text-green-700`}>{fmtNum(data.total_kl)} kL</p>
          </div>
        )}
        <div className={`bg-orange-50 border border-orange-200 rounded-xl ${tile}`}>
          <p className="text-xs uppercase tracking-wider text-orange-600 mb-1">{isWindow ? 'Total Usage Cost' : 'Total Billing'}</p>
          <p className={`${tileValue} font-bold text-orange-700`}>{fmtCurrency(totalAmount)}</p>
        </div>
      </div>

      {/* Consumption Breakdown — split into Supply / Consumers (Active + Vacant)
          per the building's reporting convention. CA / bulk-supply pockets are
          grouped under Supply since they represent the building's incoming
          utility supply, not tenant consumption. */}
      {(() => {
        const supply = data.pockets.filter(isSupply);
        const active = data.pockets.filter(isActive);
        const vacant = data.pockets.filter(isVacant);
        const showCA = data.pockets.some(p => p.common_area_kwh > 0);
        // Water gets its own table per section (see renderTable), shown when
        // any pocket in that section has water. Supply pockets often hold both
        // an electric bulk and a water bulk meter.

        const renderTable = (
          rows: typeof data.pockets,
          opts: { title: string; subtitle?: string; emptyMsg: string; tone: 'supply' | 'active' | 'vacant' }
        ) => {
          const toneStyles = {
            supply:  { bar: 'bg-purple-50 border-purple-200', text: 'text-purple-700', dot: 'bg-purple-500' },
            active:  { bar: 'bg-green-50 border-green-200', text: 'text-green-700', dot: 'bg-green-500' },
            vacant:  { bar: 'bg-amber-50 border-amber-200', text: 'text-amber-700', dot: 'bg-amber-500' },
          }[opts.tone];

          return (
            <div {...block('bg-white rounded-xl border border-gray-200 overflow-hidden')}>
              <div className={`px-4 py-3 border-b ${toneStyles.bar} flex items-center gap-3`}>
                <span className={`w-2 h-2 rounded-full ${toneStyles.dot}`} />
                <h4 className={`text-sm font-semibold ${toneStyles.text}`}>{opts.title}</h4>
                {opts.subtitle && <span className="text-xs text-gray-500">{opts.subtitle}</span>}
                <span className="ml-auto text-xs text-gray-500">{rows.length} pocket{rows.length === 1 ? '' : 's'}</span>
              </div>
              {rows.length === 0 ? (
                <div className="p-6 text-center text-sm text-gray-500">{opts.emptyMsg}</div>
              ) : (
                (() => {
                  const isSupplyTone = opts.tone === 'supply';
                  const waterRows = rows.filter(hasWater);
                  const showWater = waterRows.length > 0;
                  const amountLabel = (isWindow ? 'Usage Cost' : 'Amount') + (showWater ? ' (all utilities)' : '');
                  const th = `${pad} text-xs font-medium text-gray-500 uppercase`;
                  // A small label above each table, only when there are two of them.
                  const utilityLabel = (label: string) => showWater && (
                    <div className="px-4 pt-3 pb-1 text-xs font-semibold text-gray-600">{label}</div>
                  );
                  return (
                    <>
                      {utilityLabel('Electricity')}
                      <div className="overflow-x-auto">
                        <table className={`w-full ${tableText}`} aria-label={`${opts.title}: electricity`}>
                          <thead className="bg-gray-50">
                            <tr>
                              <th className={`${th} text-left`}>Unit</th>
                              <th className={`${th} text-left`}>{isSupplyTone ? 'Source' : 'Tenant'}</th>
                              <th className={`${th} text-left`}>Tariff</th>
                              <th className={`${th} text-right`}>Elec kWh</th>
                              {showCA && !isSupplyTone && <th className={`${th} text-right`}>CA kWh</th>}
                              <th className={`${th} text-right`}>Total kWh</th>
                              <th className={`${th} text-right`}>{amountLabel}</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {rows.map(p => (
                              <tr key={p.pocket_id} className="hover:bg-gray-50">
                                <td className={`${pad} font-medium whitespace-nowrap`}>{p.shop_no || '-'}</td>
                                <td className={`${pad}`}>{p.tenant_name || p.pocket_name}</td>
                                <td className={`${pad}`}>{tariffPill(p.tariff_names_kwh ?? (p.tariff_name_kwh !== undefined ? p.tariff_name_kwh : p.tariff_name), print)}</td>
                                <td className={`${pad} text-right font-mono`}>{fmtNum(p.current_kwh)}</td>
                                {showCA && !isSupplyTone && (
                                  <td className={`${pad} text-right font-mono`}>{p.common_area_kwh > 0 ? fmtNum(p.common_area_kwh) : '-'}</td>
                                )}
                                <td className={`${pad} text-right font-mono font-medium`}>{fmtNum(p.total_kwh)}</td>
                                <td className={`${pad} text-right font-mono font-semibold${print ? ' whitespace-nowrap' : ''}`}>{amount(p)}</td>
                              </tr>
                            ))}
                            <tr className="font-semibold bg-gray-50">
                              <td className={`${pad}`} colSpan={3}>Subtotal</td>
                              <td className={`${pad} text-right font-mono`}>{fmtNum(rows.reduce((s, p) => s + p.current_kwh, 0))}</td>
                              {showCA && !isSupplyTone && (
                                <td className={`${pad} text-right font-mono`}>{fmtNum(rows.reduce((s, p) => s + p.common_area_kwh, 0))}</td>
                              )}
                              <td className={`${pad} text-right font-mono`}>{fmtNum(rows.reduce((s, p) => s + p.total_kwh, 0))}</td>
                              <td className={`${pad} text-right font-mono${print ? ' whitespace-nowrap' : ''}`}>{fmtCurrency(rows.reduce((s, p) => s + p.total_amount_cents, 0))}{rows.some(p => p.unpriced_count) ? '*' : ''}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      {showWater && (() => {
                        // Decided per section, from its own rows: the API never gives a
                        // supply pocket a CA share, so Supply shows no CA kL column.
                        const showCaKl = waterRows.some(p => (p.common_area_kl || 0) > 0);
                        const totalKl = (p: ReportPocket) => (p.current_kl || 0) + (p.common_area_kl || 0);
                        const sum = (f: (p: ReportPocket) => number) => waterRows.reduce((s, p) => s + f(p), 0);
                        return (
                          <>
                            {utilityLabel('Water')}
                            <div className="overflow-x-auto border-t border-gray-200">
                              <table className={`w-full ${tableText}`} aria-label={`${opts.title}: water`}>
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className={`${th} text-left`}>Unit</th>
                                    <th className={`${th} text-left`}>{isSupplyTone ? 'Source' : 'Tenant'}</th>
                                    <th className={`${th} text-left`}>Tariff</th>
                                    <th className={`${th} text-right`}>Water kL</th>
                                    {showCaKl && <th className={`${th} text-right`}>CA kL</th>}
                                    <th className={`${th} text-right`}>Total kL</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                  {waterRows.map(p => (
                                    <tr key={p.pocket_id} className="hover:bg-gray-50">
                                      <td className={`${pad} font-medium whitespace-nowrap`}>{p.shop_no || '-'}</td>
                                      <td className={`${pad}`}>{p.tenant_name || p.pocket_name}</td>
                                      <td className={`${pad}`}>{tariffPill(p.tariff_names_kl ?? p.tariff_name_kl, print)}</td>
                                      <td className={`${pad} text-right font-mono`}>{fmtNum(p.current_kl || 0, 2)}</td>
                                      {showCaKl && (
                                        <td className={`${pad} text-right font-mono`}>{(p.common_area_kl || 0) > 0 ? fmtNum(p.common_area_kl, 2) : '-'}</td>
                                      )}
                                      <td className={`${pad} text-right font-mono font-medium`}>{fmtNum(totalKl(p), 2)}</td>
                                    </tr>
                                  ))}
                                  <tr className="font-semibold bg-gray-50">
                                    <td className={`${pad}`} colSpan={3}>Subtotal</td>
                                    <td className={`${pad} text-right font-mono`}>{fmtNum(sum(p => p.current_kl || 0), 2)}</td>
                                    {showCaKl && (
                                      <td className={`${pad} text-right font-mono`}>{fmtNum(sum(p => p.common_area_kl || 0), 2)}</td>
                                    )}
                                    <td className={`${pad} text-right font-mono`}>{fmtNum(sum(totalKl), 2)}</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </>
                        );
                      })()}
                    </>
                  );
                })()
              )}
            </div>
          );
        };

        return (
          <div className="space-y-6">
            {/* Supply section */}
            <section>
              <div className={`flex items-baseline gap-3 mb-3${print ? ' break-after-avoid' : ''}`}>
                <h3 className="text-base font-semibold text-gray-900">Supply</h3>
                <span className="text-xs text-gray-500">building incoming meters & common areas</span>
              </div>
              {renderTable(supply, {
                title: 'Common Area / Bulk Supply',
                emptyMsg: 'No supply meters configured for this building.',
                tone: 'supply',
              })}

              {/* Reconciliation: bulk vs allocated, split per utility. Each
                  CA meter (electricity, water) is reconciled separately so
                  unit mixing doesn't muddy the leftover. At 100% pocket
                  ca_percent the unallocated row reads ~0; under that, it
                  shows the kWh / kL the building owner is absorbing.
                  Note: stale bills (calculated before all data was logged)
                  may show false residuals — regenerate the bill run to
                  reconcile. */}
              {data.reconciliation && (() => {
                const r = data.reconciliation;
                const utilities: Array<{ label: string; unit: string; bulk: number; alloc: number; unalloc: number }> = [];
                if ((r.bulk_supply_kwh ?? 0) > 0) {
                  utilities.push({
                    label: 'Electricity',
                    unit: 'kWh',
                    bulk: r.bulk_supply_kwh ?? 0,
                    alloc: r.allocated_ca_kwh,
                    unalloc: r.unallocated_kwh,
                  });
                }
                if (r.bulk_supply_kl > 0) {
                  utilities.push({
                    label: 'Water',
                    unit: 'kL',
                    bulk: r.bulk_supply_kl,
                    alloc: r.allocated_ca_kl,
                    unalloc: r.unallocated_kl,
                  });
                }
                if (utilities.length === 0) return null;

                return (
                  <div {...block('mt-4 bg-white rounded-xl border border-gray-200 overflow-hidden')}>
                    <div className="px-4 py-3 border-b bg-gray-50 flex items-center gap-3">
                      <h4 className="text-sm font-semibold text-gray-700">Allocation Reconciliation</h4>
                      <span className="text-xs text-gray-500">bulk supply vs CA distributed to tenants</span>
                    </div>
                    <table className={`w-full ${tableText}`}>
                      <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                        <tr>
                          <th className={`${pad} text-left font-medium`}>Utility</th>
                          <th className={`${pad} text-right font-medium`}>Bulk Supply</th>
                          <th className={`${pad} text-right font-medium`}>Allocated to Tenants</th>
                          <th className={`${pad} text-right font-medium`}>Unallocated</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {utilities.map(u => {
                          const fullyAllocated = u.unalloc / Math.max(u.bulk, 1) < 0.01;
                          return (
                            <tr key={u.label} className={fullyAllocated ? 'bg-green-50' : 'bg-amber-50'}>
                              <td className={`${pad} font-medium`}>{u.label}</td>
                              <td className={`${pad} text-right font-mono`}>{fmtNum(u.bulk)} <span className="text-xs text-gray-500">{u.unit}</span></td>
                              <td className={`${pad} text-right font-mono`}>− {fmtNum(u.alloc)} <span className="text-xs text-gray-500">{u.unit}</span></td>
                              <td className={`${pad} text-right font-mono font-semibold ${fullyAllocated ? 'text-green-700' : 'text-amber-800'}`}>
                                {fmtNum(u.unalloc)} <span className="text-xs">{u.unit}</span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </section>

            {/* Consumers section (Active + Vacant) */}
            <section>
              <div className={`flex items-baseline gap-3 mb-3${print ? ' break-after-avoid' : ''}`}>
                <h3 className="text-base font-semibold text-gray-900">Consumers</h3>
                <span className="text-xs text-gray-500">tenant pockets sub-metered off the building supply</span>
              </div>
              <div className="space-y-4">
                {renderTable(active, {
                  title: 'Active',
                  emptyMsg: 'No active tenants in this building.',
                  tone: 'active',
                })}
                {renderTable(vacant, {
                  title: 'Vacant',
                  emptyMsg: 'No vacant pockets. All consumers are tenanted.',
                  tone: 'vacant',
                })}
              </div>
            </section>
          </div>
        );
      })()}

      {/* Month-over-month comparison (if available) */}
      {data.comparison_available && (
        <div {...block('bg-white rounded-xl border border-gray-200 p-6')}>
          <h3 className="text-sm font-semibold text-gray-900 mb-4">
            Period-over-Period Comparison
          </h3>
          <table className={`w-full ${tableText}`}>
            <thead className="bg-gray-50">
              <tr>
                <th className={`${pad} text-left text-xs font-medium text-gray-500 uppercase`}>Unit</th>
                <th className={`${pad} text-left text-xs font-medium text-gray-500 uppercase`}>Tenant</th>
                <th className={`${pad} text-right text-xs font-medium text-gray-500 uppercase`}>Previous kWh</th>
                <th className={`${pad} text-right text-xs font-medium text-gray-500 uppercase`}>Current kWh</th>
                <th className={`${pad} text-right text-xs font-medium text-gray-500 uppercase`}>Variance %</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {comparable.map(p => (
                <tr key={p.pocket_id} className="hover:bg-gray-50">
                  <td className={`${pad} font-medium whitespace-nowrap`}>{p.shop_no || '-'}</td>
                  <td className={`${pad}`}>{p.tenant_name || p.pocket_name}</td>
                  <td className={`${pad} text-right font-mono`}>{fmtNum(p.prev_kwh!, 0)}</td>
                  <td className={`${pad} text-right font-mono`}>{fmtNum(p.current_kwh, 0)}</td>
                  <td className={`${pad} text-right font-mono font-semibold`}>
                    {p.variance_percent !== null ? (
                      <span className={
                        p.variance_percent > 10 ? 'text-red-600' :
                        p.variance_percent < -10 ? 'text-green-600' : 'text-gray-600'
                      }>
                        {p.variance_percent > 0 ? '+' : ''}{fmtNum(p.variance_percent, 1)}%
                      </span>
                    ) : '-'}
                  </td>
                </tr>
              ))}
              <tr className="font-semibold bg-gray-50">
                <td className={`${pad}`} colSpan={2}>Total</td>
                <td className={`${pad} text-right font-mono`}>
                  {fmtNum(comparable.reduce((s, p) => s + (p.prev_kwh || 0), 0))}
                </td>
                <td className={`${pad} text-right font-mono`}>
                  {fmtNum(comparable.reduce((s, p) => s + p.current_kwh, 0))}
                </td>
                <td className={`${pad} text-right font-mono`}>
                  {(() => {
                    const prevTotal = comparable.reduce((s, p) => s + (p.prev_kwh || 0), 0);
                    const curTotal = comparable.reduce((s, p) => s + p.current_kwh, 0);
                    const pct = prevTotal > 0 ? ((curTotal - prevTotal) / prevTotal) * 100 : 0;
                    return (
                      <span className={pct > 10 ? 'text-red-600' : pct < -10 ? 'text-green-600' : 'text-gray-600'}>
                        {pct > 0 ? '+' : ''}{fmtNum(pct, 1)}%
                      </span>
                    );
                  })()}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Visual Analysis */}
      {(elecBarData.length > 0 || waterBarData.length > 0 || pieData.length > 1 || occupancyData.length > 0) && (
        <div data-testid="report-charts" className={print ? 'grid grid-cols-1 gap-4' : 'grid grid-cols-1 lg:grid-cols-2 gap-6'}>
          {elecBarData.length > 0 && (
            <div {...block('bg-white rounded-xl border border-gray-200 p-6')}>
              <h3 className="text-sm font-semibold text-gray-900 mb-4">
                Electricity by Tenant (kWh)
              </h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={elecBarData} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v) => `${fmtNum(Number(v))} kWh`} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar isAnimationActive={false} stackId="elec" dataKey="own" fill="#3B82F6" name="Own usage" radius={[0, 0, 0, 0]} />
                  <Bar isAnimationActive={false} stackId="elec" dataKey="ca"  fill="#A855F7" name="CA allocation" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {waterBarData.length > 0 && (
            <div {...block('bg-white rounded-xl border border-gray-200 p-6')}>
              <h3 className="text-sm font-semibold text-gray-900 mb-4">
                Water by Tenant (kL)
              </h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={waterBarData} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v) => `${fmtNum(Number(v))} kL`} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar isAnimationActive={false} stackId="water" dataKey="own" fill="#06B6D4" name="Own usage" radius={[0, 0, 0, 0]} />
                  <Bar isAnimationActive={false} stackId="water" dataKey="ca"  fill="#A855F7" name="CA allocation" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {pieData.length > 1 && (
            <div {...block('bg-white rounded-xl border border-gray-200 p-6')}>
              <h3 className="text-sm font-semibold text-gray-900 mb-4">
                {isWindow ? 'Usage Cost Distribution by Tenant' : 'Billing Distribution by Tenant'}
              </h3>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie isAnimationActive={false} data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={95} paddingAngle={2} dataKey="value">
                    {pieData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => fmtCurrency(Math.round(Number(v) * 100))} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}

          {occupancyData.length > 0 && (
            <div {...block('bg-white rounded-xl border border-gray-200 p-6')}>
              <h3 className="text-sm font-semibold text-gray-900 mb-4">
                Unit Occupancy
              </h3>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    isAnimationActive={false}
                    data={occupancyData}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={95}
                    paddingAngle={2}
                    dataKey="value"
                    label={(entry) => `${entry.name}: ${entry.value}`}
                  >
                    {occupancyData.map((d, i) => (
                      <Cell key={i} fill={OCCUPANCY_COLORS[d.name]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v, name) => [`${v} unit${Number(v) === 1 ? '' : 's'}`, name]} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
              <p className="text-xs text-gray-500 text-center mt-2">
                {activeCount + vacantCount} consumer pocket{activeCount + vacantCount === 1 ? '' : 's'}
                {activeCount + vacantCount > 0 && ` · ${Math.round((activeCount / (activeCount + vacantCount)) * 100)}% occupied`}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
